import { Printer } from "lucide-react";
import type { ReactNode } from "react";

export interface GuideIndexItem {
  id: string;
  title: string;
}

export function GuideActions() {
  const printGuide = () => window.print();

  return (
    <div className="guide-actions flex flex-wrap gap-3">
      <button
        type="button"
        onClick={printGuide}
        className="focus-ring inline-flex items-center gap-2 rounded-full bg-ktd-gold px-5 py-3 text-sm font-extrabold text-ktd-black shadow-gold"
      >
        <Printer className="size-4" />
        Print / Save as PDF
      </button>
    </div>
  );
}

export function GuideToc({ items }: { items: GuideIndexItem[] }) {
  return (
    <section className="guide-section ivory-card rounded-lg p-6 sm:p-8">
      <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#8a6c14]">Index</p>
      <h2 className="mt-3 font-display text-3xl font-bold text-ktd-rich">Table of contents</h2>
      <div className="guide-toc mt-6 grid gap-2 md:grid-cols-2">
        {items.map((item, index) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className="focus-ring rounded-lg border border-ktd-gold/20 bg-white px-4 py-3 text-sm font-bold leading-6 text-ktd-rich hover:border-ktd-gold/50"
          >
            {index + 1}. {item.title}
          </a>
        ))}
      </div>
    </section>
  );
}

export function GuideSection({
  id,
  title,
  children,
  eyebrow,
}: {
  id: string;
  title: string;
  children: ReactNode;
  eyebrow?: string;
}) {
  return (
    <section id={id} className="guide-section ivory-card scroll-mt-24 rounded-lg p-6 sm:p-8">
      {eyebrow && <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#8a6c14]">{eyebrow}</p>}
      <h2 className="mt-2 font-display text-3xl font-bold text-ktd-rich">{title}</h2>
      <div className="mt-5 grid gap-4 text-sm leading-7 text-ktd-muted">{children}</div>
    </section>
  );
}

export function StepList({ items }: { items: string[] }) {
  return (
    <ol className="grid gap-3">
      {items.map((item, index) => (
        <li key={item} className="flex gap-3 rounded-lg bg-white p-4 font-semibold leading-6 text-ktd-rich">
          <span className="grid size-7 shrink-0 place-items-center rounded-full bg-ktd-black text-xs text-ktd-softGold">
            {index + 1}
          </span>
          {item}
        </li>
      ))}
    </ol>
  );
}

export function Checklist({ items }: { items: string[] }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {items.map((item) => (
        <label key={item} className="flex items-start gap-3 rounded-lg bg-white p-4 font-semibold leading-6 text-ktd-rich">
          <input type="checkbox" className="mt-1 size-4 shrink-0 accent-ktd-gold" />
          {item}
        </label>
      ))}
    </div>
  );
}

export function QuickReference({ items }: { items: Array<{ label: string; detail: string }> }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {items.map((item) => (
        <div key={item.label} className="rounded-lg border border-ktd-gold/20 bg-white p-4">
          <h3 className="text-sm font-extrabold text-ktd-rich">{item.label}</h3>
          <p className="mt-2 text-sm leading-6 text-ktd-muted">{item.detail}</p>
        </div>
      ))}
    </div>
  );
}

export function Glossary({ terms }: { terms: Array<{ term: string; definition: string }> }) {
  return (
    <dl className="grid gap-3 md:grid-cols-2">
      {terms.map((item) => (
        <div key={item.term} className="rounded-lg border border-ktd-gold/20 bg-white p-4">
          <dt className="text-sm font-extrabold text-ktd-rich">{item.term}</dt>
          <dd className="mt-2 text-sm leading-6 text-ktd-muted">{item.definition}</dd>
        </div>
      ))}
    </dl>
  );
}

export function GuideCallout({
  title,
  children,
  tone = "light",
}: {
  title: string;
  children: ReactNode;
  tone?: "light" | "dark";
}) {
  return (
    <aside
      className={
        tone === "dark"
          ? "rounded-lg bg-ktd-black p-5 text-white shadow-luxe"
          : "rounded-lg border border-ktd-gold/25 bg-white p-5 text-ktd-rich"
      }
    >
      <h3 className={tone === "dark" ? "font-display text-2xl font-bold text-white" : "font-display text-2xl font-bold text-ktd-rich"}>
        {title}
      </h3>
      <div className={tone === "dark" ? "mt-3 grid gap-3 text-sm leading-7 text-white/70" : "mt-3 grid gap-3 text-sm leading-7 text-ktd-muted"}>
        {children}
      </div>
    </aside>
  );
}
